import { createStyles } from 'antd-style'

// NOTE: sider menu styles in `siderMenu` are paired with
// `features/account/layout/styles.ts` — keep `.ant-menu-item` height/
// lineHeight/borderRadius/fontWeight and the `.ant-menu-item-selected`
// colors in sync between the two shells.
export const useStyles = createStyles(({ token }) => ({
  // `--impersonation-offset` is 0px normally; while impersonating the banner
  // (see ImpersonationBanner) sets it to its height so the shell and the sticky
  // sidebar drop below the banner instead of hiding under it.
  shell: {
    minHeight: 'calc(100vh - var(--impersonation-offset, 0px))'
  },

  sider: {
    background: token.colorBgContainer,
    borderInlineEnd: `1px solid ${token.colorBorderSecondary}`,

    '& .ant-layout-sider-children': {
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100vh - var(--impersonation-offset, 0px))',
      position: 'sticky',
      top: 'var(--impersonation-offset, 0px)'
    }
  },

  siderTop: {
    height: 48,
    minHeight: 48,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    padding: `0 ${token.paddingXS}px`,
    borderBlockEnd: `1px solid ${token.colorBorderSecondary}`
  },

  siderMenu: {
    flex: '1 1 auto',
    overflowY: 'auto',
    padding: `${token.paddingXS}px 0`,
    borderInlineEnd: 'none !important',

    '& .ant-menu-item-group-title': {
      padding: '10px 16px 4px',
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: token.colorTextTertiary
    },

    '& .ant-menu-item-group + .ant-menu-item-group .ant-menu-item-group-title':
      {
        marginTop: 4
      },

    '& .ant-menu-item': {
      marginInline: token.paddingXS,
      marginBlock: 2,
      width: `calc(100% - ${token.paddingXS * 2}px)`,
      height: 36,
      lineHeight: '36px',
      borderRadius: token.borderRadius,
      fontWeight: 500
    },

    '& .ant-menu-item .ant-menu-item-icon, & .ant-menu-item .anticon': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 18,
      height: 18,
      flexShrink: 0
    },

    '&.ant-menu-inline-collapsed': {
      '& .ant-menu-item': {
        paddingInline: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      },

      '& .ant-menu-item .ant-menu-title-content': {
        display: 'none'
      },

      '& .ant-menu-item .ant-menu-item-icon, & .ant-menu-item .anticon': {
        margin: 0
      },

      '& .ant-menu-item-group-title': {
        display: 'none'
      }
    },

    '& .ant-menu-item-disabled': {
      cursor: 'not-allowed !important',
      opacity: 0.45
    },

    '& .ant-menu-item-disabled .ant-menu-title-content': {
      color: `${token.colorTextDisabled} !important`
    },

    '& .ant-menu-item-selected': {
      backgroundColor: token.colorFillTertiary,
      color: token.colorPrimaryText
    }
  },

  siderFooter: {
    borderBlockStart: `1px solid ${token.colorBorderSecondary}`,
    padding: token.paddingXXS
  },

  languageSelect: {
    width: 'auto',
    minWidth: 72,

    '& .ant-select-selector': {
      paddingInline: `${token.paddingXXS}px ${token.paddingXS}px !important`,
      borderRadius: token.borderRadius,
      border: `1px solid ${token.colorBorderSecondary} !important`,
      background: `${token.colorBgContainer} !important`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: 'none !important'
    },

    '& .ant-select-selection-item': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },

    '& .ant-select-arrow': {
      display: 'none'
    },

    '&:hover .ant-select-selector, &.ant-select-focused .ant-select-selector': {
      borderColor: `${token.colorPrimaryBorder} !important`,
      background: `${token.colorFillTertiary} !important`
    }
  },

  languageOption: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: token.marginXS,
    fontSize: token.fontSize
  },

  languageTrigger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: token.marginXS,
    color: token.colorText,
    fontSize: token.fontSizeSM,
    fontWeight: token.fontWeightStrong as number,
    lineHeight: 1
  },

  languageTriggerCode: {
    letterSpacing: '0.04em',
    fontVariantNumeric: 'tabular-nums'
  },

  switcherTrigger: {
    display: 'flex',
    alignItems: 'center',
    gap: token.marginXS,
    width: '100%',
    padding: `${token.paddingXXS}px ${token.paddingXS}px`,
    borderRadius: token.borderRadius,
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    textAlign: 'left',
    color: token.colorText,
    transition: `background-color ${token.motionDurationMid}`,
    lineHeight: 1.15,

    '&:hover': {
      backgroundColor: token.colorFillTertiary
    },

    '&:focus-visible': {
      outline: 'none',
      boxShadow: `0 0 0 2px ${token.colorPrimaryBg}`
    }
  },

  switcherTriggerCollapsed: {
    justifyContent: 'center',
    padding: token.paddingXXS
  },

  switcherAvatar: {
    width: 26,
    minWidth: 26,
    height: 26,
    borderRadius: token.borderRadius,
    background: `linear-gradient(135deg, ${token.colorPrimary} 0%, ${token.colorPrimaryHover} 100%)`,
    color: token.colorWhite,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: token.fontWeightStrong,
    fontSize: token.fontSizeSM,
    letterSpacing: 0.3,
    flexShrink: 0,
    overflow: 'hidden'
  },

  /**
   * One-time welcome. Sits above every popup layer — it covers the shell while
   * the workspace loads behind it — and passes clicks through to its own
   * dismiss handler rather than to the app underneath.
   */
  welcomeRoot: {
    position: 'fixed',
    inset: 0,
    zIndex: token.zIndexPopupBase + 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },

  welcomeBackdrop: {
    position: 'absolute',
    inset: 0,
    background: token.colorBgLayout
  },

  welcomeColumn: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: token.margin,
    pointerEvents: 'none'
  },

  /** Reserves the tile's slot in the column; the tile itself is drawn over it. */
  welcomeTileAnchor: {
    width: 64,
    height: 64
  },

  welcomeText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: token.marginXXS,
    textAlign: 'center'
  },

  /**
   * Positioned from measured coordinates rather than laid out, so it can travel
   * from the column to the sidebar tile in one continuous transform.
   */
  welcomeTile: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: 64,
    height: 64,
    transformOrigin: 'top left',
    borderRadius: token.borderRadiusLG,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: token.colorWhite,
    fontWeight: token.fontWeightStrong,
    fontSize: token.fontSizeHeading3,
    letterSpacing: 0.3
  },
  /** Same reasoning as `switcherAvatarPhoto`: a logo needs its own light ground. */
  welcomeTilePhoto: {
    background: token.colorBgContainer,
    border: `1px solid ${token.colorBorderSecondary}`,
    overflow: 'hidden'
  },

  welcomeTileImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain' as const,
    display: 'block',
    padding: 6
  },

  switcherPlaceholderAvatar: {
    width: 26,
    minWidth: 26,
    height: 26,
    borderRadius: token.borderRadius,
    background: token.colorFillSecondary,
    color: token.colorTextSecondary,
    border: `1px dashed ${token.colorBorder}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },

  switcherInfo: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flex: 1,
    lineHeight: 1.2
  },

  switcherName: {
    fontSize: 13,
    fontWeight: token.fontWeightStrong,
    color: token.colorText,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: 1.15
  },

  switcherMeta: {
    fontSize: 11,
    color: token.colorTextSecondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: 1.15
  },

  switcherChevron: {
    color: token.colorTextSecondary,
    fontSize: 10
  },

  switcherDropdown: {
    width: 250,

    '& .ant-dropdown-menu': {
      padding: token.paddingXXS
    },

    '& .ant-dropdown-menu .ant-dropdown-menu-item-group-title': {
      fontSize: 10,
      fontWeight: token.fontWeightStrong,
      color: token.colorTextTertiary,
      padding: `${token.paddingXXS}px ${token.paddingSM}px ${token.paddingXS}px`,
      marginBottom: token.marginXXS,
      borderBottom: `1px solid ${token.colorSplit}`
    },

    // Cap the workspace list at ~5 items; footer actions stay pinned below.
    // Scrollbar appearance comes from the global rules in
    // `src/theme/scrollbars.css` — don't re-declare it here.
    '& .ant-dropdown-menu .ant-dropdown-menu-item-group-list': {
      margin: 0,
      maxHeight: 200,
      overflowY: 'auto',
      overscrollBehavior: 'contain'
    }
  },

  switcherListItem: {
    display: 'flex',
    alignItems: 'center',
    gap: token.marginXS
  },

  switcherListItemInfo: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flex: 1,
    lineHeight: 1.2
  },

  switcherDemoBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    alignSelf: 'center',
    flexShrink: 0
  },

  /**
   * Tile carrying a logo rather than initials. The brand gradient is the ground
   * for two white letters; a teal mark on it disappears, so the tile turns into
   * a plain light chip and lets the logo be the only thing in it.
   */
  switcherAvatarPhoto: {
    background: `${token.colorBgContainer} !important`,
    border: `1px solid ${token.colorBorderSecondary}`,
    padding: 2
  },

  /** See `heroAvatarImage`: a logo is shown whole, not cropped to the tile. */
  switcherAvatarImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain' as const,
    display: 'block'
  },

  switcherListAvatar: {
    width: 28,
    minWidth: 28,
    height: 28,
    borderRadius: token.borderRadius,
    color: token.colorWhite,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: token.fontSizeSM,
    fontWeight: token.fontWeightStrong,
    flexShrink: 0,
    overflow: 'hidden'
  },

  topbar: {
    background: token.colorBgContainer,
    borderBlockEnd: `1px solid ${token.colorBorderSecondary}`,
    padding: `0 ${token.padding}px`,
    height: 48,
    minHeight: 48,
    lineHeight: '1 !important' as unknown as string
  },

  topbarDividerY: {
    width: 1,
    alignSelf: 'stretch',
    height: 24,
    background: token.colorBorderSecondary,
    marginInline: token.marginXXS
  },

  topbarWorkspaceChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 26,
    paddingInline: 10,
    borderRadius: 999,
    background: token.colorFillTertiary,
    border: `1px solid ${token.colorBorderSecondary}`,
    color: token.colorText,
    fontSize: token.fontSizeSM,
    lineHeight: 1,
    cursor: 'pointer',
    transition: `background-color ${token.motionDurationMid}, border-color ${token.motionDurationMid}, color ${token.motionDurationMid}`,
    textDecoration: 'none',
    maxWidth: 280,

    '&:hover': {
      background: token.colorFillSecondary,
      borderColor: token.colorPrimaryBorder,
      color: token.colorPrimaryText
    },

    '&:focus-visible': {
      outline: 'none',
      boxShadow: `0 0 0 2px ${token.colorPrimaryBg}`
    }
  },

  topbarWorkspaceChipUrl: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
    fontWeight: token.fontWeightStrong as number
  },

  topbarDocsLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 26,
    paddingInline: 8,
    borderRadius: token.borderRadius,
    color: token.colorTextSecondary,
    fontSize: token.fontSizeSM,
    lineHeight: 1,
    textDecoration: 'none',
    transition: `background-color ${token.motionDurationMid}, color ${token.motionDurationMid}`,

    '&:hover': {
      background: token.colorFillTertiary,
      color: token.colorText
    },

    '&:focus-visible': {
      outline: 'none',
      boxShadow: `0 0 0 2px ${token.colorPrimaryBg}`
    }
  },

  topbarUser: {
    paddingInline: token.paddingXS
  },

  topbarUserInfo: {
    display: 'flex',
    flexDirection: 'column',
    lineHeight: 1.15,
    marginInlineStart: token.marginXS
  },

  topbarUserName: {
    fontSize: token.fontSizeSM,
    fontWeight: token.fontWeightStrong,
    color: token.colorText,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 160
  },

  topbarUserRole: {
    fontSize: token.fontSizeSM,
    color: token.colorTextSecondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 160
  },

  topbarUserMenu: {
    minWidth: 260,
    background: token.colorBgElevated,
    borderRadius: token.borderRadiusLG,
    boxShadow: token.boxShadowSecondary,
    overflow: 'hidden'
  },

  topbarUserMenuHeader: {
    padding: token.padding
  },

  content: {
    padding: `${token.padding}px ${token.paddingLG}px ${token.paddingLG}px`,
    background: token.colorBgLayout
  },

  pageHeader: {
    marginBottom: token.margin
  },

  pageBreadcrumb: {
    fontSize: token.fontSizeSM,
    marginBottom: token.marginXXS
  },

  pageTitle: {
    marginBottom: '0 !important' as unknown as string,
    fontSize: 22,
    fontWeight: 600,
    lineHeight: 1.3
  },

  pageSubtitle: {
    fontSize: token.fontSizeSM,
    color: token.colorTextSecondary
  },

  footer: {
    background: token.colorBgContainer,
    borderBlockStart: `1px solid ${token.colorBorderSecondary}`,
    padding: `${token.paddingXS}px ${token.paddingLG}px`,
    fontSize: 11,
    color: token.colorTextTertiary
  },

  footerInner: {
    justifyContent: 'space-between',
    rowGap: token.paddingXXS
  },

  footerMeta: {
    minWidth: 0
  },

  footerCopy: {
    fontSize: 11,
    color: token.colorTextTertiary,
    lineHeight: 1.4
  },

  footerSep: {
    fontSize: 11,
    color: token.colorTextQuaternary,
    userSelect: 'none' as const
  },

  footerVersion: {
    fontSize: 11,
    color: token.colorTextSecondary,
    fontVariantNumeric: 'tabular-nums',
    fontFamily:
      'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace'
  },

  footerLinks: {
    flex: '1 1 auto',
    justifyContent: 'center',
    rowGap: token.paddingXXS
  },

  footerLink: {
    fontSize: 11,
    color: token.colorTextSecondary,
    padding: `0 ${token.paddingXS}px`,
    transition: `color ${token.motionDurationMid}`,

    '&:hover': {
      color: token.colorPrimaryText
    }
  },

  footerLinkDivider: {
    margin: 0,
    height: 10,
    borderInlineStartColor: token.colorBorderSecondary
  },

  footerPowered: {
    minWidth: 0
  },

  footerHeart: {
    color: token.colorError,
    flex: '0 0 auto'
  },

  footerBrand: {
    fontSize: 11,
    color: token.colorText,
    fontWeight: token.fontWeightStrong as number
  }
}))
